/**
 * Client-safe shapes and constants for the /about page's market-dependent
 * facts. The server read lives in `lib/about-facts-server.ts`.
 */

/** Internal pages the /about page links to only when they exist in a market. */
export const ABOUT_OPTIONAL_PATHS = [
  "/tests/full-body-mri",
  "/tests/advanced-blood-test",
  "/tests/prevention-starter",
  "/pricing",
  "/report",
  "/heart-health",
  "/metabolism",
  "/female-hormones",
  "/teams",
  "/contact",
  "/free-call",
  "/locations",
] as const

export type AboutOptionalPath = (typeof ABOUT_OPTIONAL_PATHS)[number]

export interface AboutFacts {
  /** BCP 47 locale for list and region formatting (e.g. "de-CH"). */
  intlLocale: string
  /**
   * The market sells MRI: the full-body MRI page exists and MRI books. Picks
   * the MRI or blood-only variant of every sentence that names the offer.
   */
  offersMri: boolean
  /** The market's base blood check-up, when its page exists. */
  bloodTest: {
    path: "/tests/advanced-blood-test" | "/tests/prevention-starter"
    /** `nav`-namespace label key for the product's name. */
    labelKey: "advancedBloodTest" | "preventionStarter"
  } | null
  /** Active clinic locations in this market; null when the CMS read is empty. */
  locationCount: number | null
  /** Live from-prices; null where the market shows none or the feed fell back. */
  prices: { blood: string; mri: string } | null
  /** Which of `ABOUT_OPTIONAL_PATHS` resolve to a live page in this market. */
  availablePaths: AboutOptionalPath[]
  /** Support line (display form), only where /contact shows a phone. */
  phone: string | null
  /** Digits for a `wa.me/<digits>` link, only where /contact shows WhatsApp. */
  whatsappDigits: string | null
}

/** Company email published on /contact, the imprint and the Organization schema. */
export const COMPANY_EMAIL = "get@aheadhealth.com"
